import { useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { userRoutingService } from '@/services/routing/UserRoutingService';
import { NavigationDestination } from '@/services/routing/types';
import { getDefaultAuthenticatedDestination } from '@/utils/domainDetection';

interface UsePostAuthRoutingOptions {
  enabled?: boolean;
  onRoutingComplete?: (destination: NavigationDestination) => void;
  onRoutingError?: (error: Error) => void;
}

/**
 * Hook that handles post-authentication routing
 */
export function usePostAuthRouting(options: UsePostAuthRoutingOptions = {}) {
  const { enabled = true, onRoutingComplete, onRoutingError } = options;
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Handle routing after login
   */
  const handlePostLoginRouting = useCallback(async () => {
    if (!user) return;

    console.log('[usePostAuthRouting] Starting post-login routing for user:', {
      userId: user._id,
      roles: user.roles,
      hasMerchantProfile: !!user.otherProfiles?.merchant
    });

    try {
      // Extract return URL from location state or query params
      // Support both 'returnUrl' and 'redirect' query parameters for compatibility
      const searchParams = new URLSearchParams(location.search);
      const returnUrl = location.state?.returnUrl || 
                       searchParams.get('returnUrl') ||
                       searchParams.get('redirect') ||
                       undefined;

      const destination = await userRoutingService.getPostLoginDestination(user, {
        returnUrl,
        metadata: location.state
      });

      console.log('[usePostAuthRouting] Routing user to:', destination.path);
      
      // Navigate to the destination
      if (destination.replace) {
        navigate(destination.path, { 
          replace: true, 
          state: destination.state 
        });
      } else {
        navigate(destination.path, { 
          state: destination.state 
        });
      }

      onRoutingComplete?.(destination);
    } catch (error) {
      console.error('[usePostAuthRouting] Routing failed:', error);
      onRoutingError?.(error as Error);
      
      // Fallback to hostname default on error
      navigate(getDefaultAuthenticatedDestination(), { replace: true });
    }
  }, [user, navigate, location, onRoutingComplete, onRoutingError]);

  /**
   * Handle routing after signup
   */
  const handlePostSignupRouting = useCallback(async () => {
    if (!user) return;

    try {
      const destination = await userRoutingService.getPostSignupDestination(user, {
        metadata: location.state
      });

      console.log('[usePostAuthRouting] Routing new user to:', destination.path);
      
      navigate(destination.path, { 
        state: destination.state 
      });

      onRoutingComplete?.(destination);
    } catch (error) {
      console.error('[usePostAuthRouting] Signup routing failed:', error);
      onRoutingError?.(error as Error);
      
      // Fallback to hostname default on error
      navigate(getDefaultAuthenticatedDestination(), { replace: true });
    }
  }, [user, navigate, location, onRoutingComplete, onRoutingError]);

  return {
    handlePostLoginRouting,
    handlePostSignupRouting,
    user
  };
}

/**
 * Hook that automatically routes on auth state change
 * Use this sparingly - prefer explicit routing in login/signup flows
 */
export function useAutoPostAuthRouting(options: UsePostAuthRoutingOptions = {}) {
  const { enabled = true } = options;
  const { user, isGuest } = useAuth();
  const { handlePostLoginRouting } = usePostAuthRouting(options);
  
  useEffect(() => {
    if (enabled && user && !isGuest) {
      // Only auto-route if we're on a login/signup page
      const authPaths = ['/login', '/signup', '/register'];
      const currentPath = window.location.pathname;
      
      if (authPaths.some(path => currentPath.startsWith(path))) {
        handlePostLoginRouting();
      }
    }
  }, [enabled, user, isGuest, handlePostLoginRouting]);
}
